import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import { Check as CheckIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import type { Invitation } from '@/types'
import { formatDateDMY } from '@/utils/date'

/**
 * Что показывает диалог выдачи кабинета.
 *
 * - `create` — форма: почта и флажок письма;
 * - `resend` — приглашение отправляется заново: пока нет ответа — скелетон,
 *   при отказе — ошибка;
 * - `result` — ссылка для ученика и отметка, ушло ли письмо.
 */
export type CabinetInvitationDialogView =
  | {
      mode: 'create'
      studentName: string
      submitting: boolean
      error: string | null
      emailError: string | null
    }
  | { mode: 'resend'; studentName: string; error: string | null }
  | { mode: 'result'; studentName: string; invitation: Invitation; emailSent: boolean }

interface CabinetInvitationDialogProps {
  open: boolean
  /** Содержимое остаётся и на время анимации закрытия; null — диалог ещё не открывали */
  view: CabinetInvitationDialogView | null
  onClose: () => void
  /** Диалог закрылся полностью — можно забыть содержимое */
  onExited: () => void
  onSubmit: (data: { email: string; sendEmail: boolean }) => void
  /** Учитель поправил почту — серверная ошибка под полем больше не про неё */
  onEmailChange: () => void
}

/** Сколько держится отметка «Ссылка скопирована» */
const COPIED_RESET_MS = 2000

/**
 * Диалог «Создать кабинет»: почта ученика, флажок письма, затем ссылка
 * с копированием — так же, как в диалоге обычного приглашения.
 */
export function CabinetInvitationDialog({
  open,
  view,
  onClose,
  onExited,
  onSubmit,
  onEmailChange,
}: CabinetInvitationDialogProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [sendEmail, setSendEmail] = useState(true)
  const [emailRequired, setEmailRequired] = useState(false)
  const [copied, setCopied] = useState(false)
  const copiedTimer = useRef<number | null>(null)

  const busy = view?.mode === 'create' && view.submitting

  // Следующее открытие — с чистой формой
  const handleExited = () => {
    setEmail('')
    setSendEmail(true)
    setEmailRequired(false)
    setCopied(false)
    onExited()
  }

  useEffect(() => () => {
    if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current)
  }, [])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const trimmed = email.trim()
    if (trimmed === '') {
      setEmailRequired(true)
      return
    }
    onSubmit({ email: trimmed, sendEmail })
  }

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      // Буфер недоступен (нет HTTPS или разрешения) — ссылку можно выделить в поле
      return
    }
    setCopied(true)
    if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current)
    copiedTimer.current = window.setTimeout(() => setCopied(false), COPIED_RESET_MS)
  }

  if (view === null) {
    return null
  }

  const title =
    view.mode === 'create'
      ? t('users.cabinetInvitation.create')
      : view.mode === 'resend'
        ? t('users.cabinetInvitation.resend')
        : t('users.cabinetInvitation.resultTitle')

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: handleExited }}
    >
      <DialogTitle>{title}</DialogTitle>
      {view.mode === 'create' ? (
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 0 }}>
            {view.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {view.error}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('users.cabinetInvitation.student', { name: view.studentName })}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('users.cabinetInvitation.explanation')}
            </Typography>
            <TextField
              fullWidth
              required
              autoFocus
              type="email"
              name="email"
              autoComplete="off"
              label={t('users.cabinetInvitation.emailLabel')}
              value={email}
              disabled={busy}
              onChange={(event) => {
                setEmail(event.target.value)
                setEmailRequired(false)
                onEmailChange()
              }}
              error={emailRequired || Boolean(view.emailError)}
              helperText={
                emailRequired
                  ? t('users.cabinetInvitation.emailRequired')
                  : view.emailError ?? t('users.cabinetInvitation.emailHint')
              }
              inputProps={{ maxLength: 255 }}
            />
            <FormControlLabel
              sx={{ mt: 1, display: 'flex' }}
              control={
                <Checkbox
                  checked={sendEmail}
                  disabled={busy}
                  onChange={(event) => setSendEmail(event.target.checked)}
                />
              }
              label={t('users.cabinetInvitation.sendEmail')}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('users.cabinetInvitation.create')}
            </Button>
          </DialogActions>
        </Box>
      ) : (
        <>
          <DialogContent sx={{ pt: 0 }}>
            {view.mode === 'resend' && view.error && (
              <Alert severity="error">{view.error}</Alert>
            )}
            {view.mode === 'resend' && !view.error && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="rounded" height={40} />
                <Skeleton variant="rounded" height={48} />
              </Box>
            )}
            {view.mode === 'result' && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('users.cabinetInvitation.student', { name: view.studentName })}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('users.cabinetInvitation.linkValidUntil', {
                    date: formatDateDMY(view.invitation.expires_at),
                  })}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={view.invitation.invitation_url}
                  inputProps={{
                    readOnly: true,
                    'aria-label': t('users.cabinetInvitation.linkLabel'),
                  }}
                  onFocus={(event) => event.target.select()}
                />
                <Button
                  startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                  onClick={() => void handleCopy(view.invitation.invitation_url)}
                  sx={{ mt: 1 }}
                >
                  {copied ? t('users.cabinetInvitation.copied') : t('invitations.copyLink')}
                </Button>
                <Alert severity={view.emailSent ? 'success' : 'info'} sx={{ mt: 2 }}>
                  {view.emailSent
                    ? t('users.cabinetInvitation.emailSent', { email: view.invitation.email })
                    : t('users.cabinetInvitation.emailNotSent', { email: view.invitation.email })}
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose} variant={view.mode === 'result' ? 'contained' : 'text'}>
              {t('common.close')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
